/**
 * Offline Storage Manager using IndexedDB
 * Handles persistent storage of lists, templates, and pending offline changes
 */

const DB_NAME = 'FamilyCartOffline';
const DB_VERSION = 2;

// Store names
const STORES = {
  LISTS: 'lists',
  TEMPLATES: 'templates',
  STORES: 'stores',
  RECIPES: 'recipes',
  SYNC_QUEUE: 'syncQueue',
  META: 'meta'
};

let db = null;

/**
 * Initialize IndexedDB
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Lists store
      if (!database.objectStoreNames.contains(STORES.LISTS)) {
        const listsStore = database.createObjectStore(STORES.LISTS, { keyPath: 'id' });
        listsStore.createIndex('user_id', 'user_id', { unique: false });
        listsStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      // Templates store
      if (!database.objectStoreNames.contains(STORES.TEMPLATES)) {
        const templatesStore = database.createObjectStore(STORES.TEMPLATES, { keyPath: 'id' });
        templatesStore.createIndex('user_id', 'user_id', { unique: false });
      }

      // Stores store
      if (!database.objectStoreNames.contains(STORES.STORES)) {
        const storesStore = database.createObjectStore(STORES.STORES, { keyPath: 'id' });
        storesStore.createIndex('user_id', 'user_id', { unique: false });
      }

      // Recipes store
      if (!database.objectStoreNames.contains(STORES.RECIPES)) {
        const recipesStore = database.createObjectStore(STORES.RECIPES, { keyPath: 'id' });
        recipesStore.createIndex('user_id', 'user_id', { unique: false });
      }

      // Sync queue for offline mutations
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('type', 'type', { unique: false });
      }

      // Meta store for last sync times, user info, etc.
      if (!database.objectStoreNames.contains(STORES.META)) {
        database.createObjectStore(STORES.META, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Generic get all from store
 */
export const getAll = async (storeName) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generic get by ID
 */
export const getById = async (storeName, id) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generic put (insert or update)
 */
export const put = async (storeName, data) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Generic put multiple items
 */
export const putAll = async (storeName, items) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    items.forEach(item => store.put(item));

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Generic delete
 */
export const remove = async (storeName, id) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all data in a store
 */
export const clearStore = async (storeName) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// ============ Lists Operations ============

export const saveLists = (lists) => putAll(STORES.LISTS, lists);
export const saveList = (list) => put(STORES.LISTS, list);
export const getLocalLists = () => getAll(STORES.LISTS);
export const getLocalList = (id) => getById(STORES.LISTS, id);
export const deleteLocalList = (id) => remove(STORES.LISTS, id);

// ============ Templates Operations ============

export const saveTemplates = (templates) => putAll(STORES.TEMPLATES, templates);
export const saveTemplate = (template) => put(STORES.TEMPLATES, template);
export const getLocalTemplates = () => getAll(STORES.TEMPLATES);
export const deleteLocalTemplate = (id) => remove(STORES.TEMPLATES, id);

// ============ Stores Operations ============

export const saveStores = (stores) => putAll(STORES.STORES, stores);
export const getLocalStores = () => getAll(STORES.STORES);

// ============ Recipes Operations ============

export const saveRecipes = (recipes) => putAll(STORES.RECIPES, recipes);
export const getLocalRecipes = () => getAll(STORES.RECIPES);
export const deleteLocalRecipe = (id) => remove(STORES.RECIPES, id);

// ============ Sync Queue Operations ============

/**
 * Add an action to the sync queue (for offline mutations)
 */
export const addToSyncQueue = async (action) => {
  await initDB();
  const queueItem = {
    ...action,
    timestamp: Date.now(),
    retries: 0
  };
  return put(STORES.SYNC_QUEUE, queueItem);
};

/**
 * Get all pending sync actions
 */
export const getSyncQueue = () => getAll(STORES.SYNC_QUEUE);

/**
 * Remove an item from sync queue after successful sync
 */
export const removeFromSyncQueue = (id) => remove(STORES.SYNC_QUEUE, id);

/**
 * Clear entire sync queue
 */
export const clearSyncQueue = () => clearStore(STORES.SYNC_QUEUE);

// ============ Meta Operations ============

export const setMeta = (key, value) => put(STORES.META, { key, value, updatedAt: Date.now() });
export const getMeta = async (key) => {
  const result = await getById(STORES.META, key);
  return result?.value;
};

// ============ Utility Functions ============

/**
 * Check if we're online
 */
export const isOnline = () => navigator.onLine;

/**
 * Get sync queue count
 */
export const getSyncQueueCount = async () => {
  const queue = await getSyncQueue();
  return queue.length;
};

/**
 * Clear all offline data (for logout)
 */
export const clearAllOfflineData = async () => {
  await initDB();
  const stores = [STORES.LISTS, STORES.TEMPLATES, STORES.STORES, STORES.RECIPES, STORES.SYNC_QUEUE, STORES.META];
  
  for (const storeName of stores) {
    await clearStore(storeName);
  }
};

export { STORES };
